//client/src/hooks/useTiposContrato.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../services/api';
import useAlert from './useAlert';

const TIPOS_CONTRATO_QUERY_KEY = 'tiposContrato';
const BASE_ROUTE = '/tipos-contrato';

// --- HOOKS DE CONSULTA (QUERY) ---
export const useTiposContrato = (params = {}) => {
    const { setAlert } = useAlert();
    return useQuery({
        queryKey: [TIPOS_CONTRATO_QUERY_KEY, params],
        queryFn: async () => {
            try {
                const { data } = await apiClient.get(BASE_ROUTE, { params });
                return data.data || []; 
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Erro ao carregar tipos de contrato.';
                setAlert(errorMsg, 'danger');
                throw error;
            }
        },
        staleTime: 1000 * 60 * 15, // 15 minutos
    });
};

// ✅ NOVO HOOK: Tipos de contrato ativos para formulários
export const useTiposContratoAtivos = (params = {}) => {
    const { setAlert } = useAlert();
    return useQuery({
        queryKey: [TIPOS_CONTRATO_QUERY_KEY, 'ativos', params],
        queryFn: async () => {
            try {
                const { data } = await apiClient.get(BASE_ROUTE, { 
                    params: { ...params, ativo: true } 
                });
                return data.data || []; 
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Erro ao carregar tipos de contrato.';
                setAlert(errorMsg, 'danger');
                throw error;
            }
        },
        staleTime: 1000 * 60 * 10, // 10 minutos
    });
};

// --- HOOKS DE AÇÃO (MUTATION) ---
export const useMutateTiposContrato = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const onMutationSuccess = (message) => {
        queryClient.invalidateQueries({ queryKey: [TIPOS_CONTRATO_QUERY_KEY] });
        setAlert(message, 'success');
    };

    const addTipoContrato = useMutation({
        mutationFn: async (tipoContratoData) => {
            try {
                const { data } = await apiClient.post(BASE_ROUTE, tipoContratoData);
                return data;
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Erro ao criar tipo de contrato.';
                throw new Error(errorMsg);
            }
        },
        onSuccess: (data) => {
            onMutationSuccess(data.message || 'Tipo de Contrato criado com sucesso!');
        },
        onError: (err) => {
            setAlert(err.message || 'Erro ao criar tipo de contrato.', 'danger');
        },
    });

    const updateTipoContrato = useMutation({
        mutationFn: async ({ id, tipoContratoData }) => {
            try {
                const { data } = await apiClient.put(`${BASE_ROUTE}/${id}`, tipoContratoData);
                return data;
            } catch (error) {
                const errorMsg = error.response?.data?.error || 'Erro ao atualizar tipo de contrato.';
                throw new Error(errorMsg);
            }
        },
        onSuccess: (data) => {
            onMutationSuccess(data.message || 'Tipo de Contrato atualizado com sucesso!');
        },
        onError: (err) => {
            setAlert(err.message || 'Erro ao atualizar tipo de contrato.', 'danger');
        },
    });

    const deleteTipoContrato = useMutation({
        mutationFn: async (id) => {
            if (window.confirm('Tem certeza que deseja apagar este tipo de contrato?')) {
                try {
                    const { data } = await apiClient.delete(`${BASE_ROUTE}/${id}`);
                    return data;
                } catch (error) {
                    const errorMsg = error.response?.data?.error || 'Erro ao apagar tipo de contrato.';
                    throw new Error(errorMsg);
                }
            }
            return Promise.reject(new Error('Ação cancelada'));
        },
        onSuccess: (data) => {
            onMutationSuccess(data.message || 'Tipo de Contrato removido com sucesso.');
        },
        onError: (err) => {
            if (err.message !== 'Ação cancelada') {
                setAlert(err.message || 'Erro ao apagar tipo de contrato.', 'danger');
            }
        },
    });
    
    const { isPending: isAdding } = addTipoContrato;
    const { isPending: isUpdating } = updateTipoContrato;
    const { isPending: isDeleting } = deleteTipoContrato;

    return { 
        addTipoContrato, 
        updateTipoContrato, 
        deleteTipoContrato,
        isPending: isAdding || isUpdating || isDeleting
    };
};